import {
  Button,
  Card,
  IncrementableNumberInput,
  ManageableItemsList,
  Modal,
  useToastContext,
  Text,
  H2,
} from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import MarkdownCore from '@payaca/components/markdownLabel/MarkdownCore';
import { FC, useCallback, useState } from 'react';

import useAddEstimateDefaultItem from '../../../api/mutations/estimates/useAddEstimateDefaultItem';
import useRemoveEstimateDefaultItem from '../../../api/mutations/estimates/useRemoveEstimateDefaultItem';
import useUpdateEstimateDefaultItem from '../../../api/mutations/estimates/useUpdateEstimateDefaultItem';
import useGetEstimateDefaultItems from '../../../api/queries/estimates/useGetEstimateDefaultItems';
import {
  EstimateCategory,
  GetEstimateDefaultItemsQuery,
} from '../../../gql/graphql';
import AddEditItemSidebar from '../../pages/listedItemsPage/components/AddEditItemSidebar';
import SelectItemModal from '../selectItemModal/SelectItemModal';

type EstimateDefaultItem =
  GetEstimateDefaultItemsQuery['estimateDefaultItems'][number];

const COPY: Record<EstimateCategory, string> = {
  HEAT_PUMP:
    'These items will appear on all your heat pump estimates by default. They can then be customised within the estimate.',
  SOLAR:
    'These items will appear on all your solar estimates by default. They can then be customised within the estimate.',
};

const QuantityCell: FC<{
  item: EstimateDefaultItem;
  isEditing: boolean;
  onCommit: () => void;
}> = (props) => {
  const { item, isEditing, onCommit } = props;

  const { pushSuccess } = useToastContext();
  const [localQuantity, setLocalQuantity] = useState(item.quantity);
  const { mutateAsync, isPending } = useUpdateEstimateDefaultItem();

  const handleCommit = useCallback(async () => {
    if (
      localQuantity != null &&
      localQuantity >= 1 &&
      localQuantity !== item.quantity
    ) {
      await mutateAsync({
        id: item.id,
        input: { quantity: localQuantity },
        category: item.category,
      });
      pushSuccess('Quantity updated');
    }
    onCommit();
  }, [
    item.id,
    item.quantity,
    item.category,
    localQuantity,
    mutateAsync,
    onCommit,
    pushSuccess,
  ]);

  if (!isEditing) {
    return <span>{item.quantity}</span>;
  }

  return (
    <div
      className="flex items-center gap-2"
      // Commit when focus leaves the entire cell. The contains() guard
      // prevents commits when focus shifts between the input and the
      // wrapper's own +/- buttons, since React's blur events bubble.
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        void handleCommit();
      }}
    >
      <IncrementableNumberInput
        sizeVariant="sm"
        wrapperClassName="w-32"
        value={localQuantity}
        min={1}
        autoFocus
        disabled={isPending}
        onChange={(value) => {
          if (value === null) return;
          setLocalQuantity(value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void handleCommit();
          }
        }}
      />
    </div>
  );
};

const DeleteEstimateDefaultItemModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  item: EstimateDefaultItem | null;
}> = (props) => {
  const { isOpen, onClose, item } = props;
  const { mutateAsync, isPending } = useRemoveEstimateDefaultItem();

  const onConfirm = useCallback(() => {
    if (!item) return;
    void mutateAsync({ id: item.id, category: item.category }).then(() =>
      onClose()
    );
  }, [item, mutateAsync, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Remove default item">
      <Modal.Body>
        Are you sure you want to remove{' '}
        <strong>{item?.lineItem.name ?? 'this item'}</strong> from your default
        items? The item will remain available in your items catalogue.
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button variant="secondary" onClick={() => onClose?.()}>
            Cancel
          </Button>
          <Button
            colour="negative"
            isProcessing={isPending}
            onClick={() => !isPending && onConfirm()}
          >
            Yes, remove
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

const DefaultEstimateItemsSection: FC<{ category: EstimateCategory }> = ({
  category,
}) => {
  const { estimateDefaultItems, isLoading } =
    useGetEstimateDefaultItems(category);
  const { mutateAsync: addEstimateDefaultItem } = useAddEstimateDefaultItem();

  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [itemToDelete, setItemToDelete] = useState<EstimateDefaultItem | null>(
    null
  );
  const [isSelectItemModalOpen, setIsSelectItemModalOpen] = useState(false);
  const [isCreateItemSidebarOpen, setIsCreateItemSidebarOpen] = useState(false);

  const items: EstimateDefaultItem[] = estimateDefaultItems ?? [];

  const disabledItemIds = items.map((item) => +item.lineItem.id);

  const onSelectItem = useCallback(
    async (lineItemId: string) => {
      await addEstimateDefaultItem({
        lineItemId,
        category,
      });
    },
    [addEstimateDefaultItem, category]
  );

  return (
    <Card sizeVariant="lg">
      <Card.Body className="flex flex-col gap-4">
        <div>
          <H2>Default items</H2>
          <Text>{COPY[category]}</Text>
        </div>

        <ManageableItemsList>
          <ManageableItemsList.HeaderBar
            heading="Items"
            buttons={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setIsSelectItemModalOpen(true)}
              >
                Add item
              </Button>
            }
          />
          <ManageableItemsList.Table
            items={items}
            uniqueKey="id"
            isLoading={isLoading}
            itemActions={[
              {
                label: 'Edit quantity',
                onClick: (item) => setEditingItemId(item.id),
              },
              {
                label: 'Delete',
                onClick: (item) => setItemToDelete(item),
              },
            ]}
            emptyText="No default items have been added yet."
          >
            <ManageableItemsList.Table.Column
              field="lineItem"
              header="Internal reference"
              render={(lineItem: EstimateDefaultItem['lineItem']) =>
                lineItem.name ?? '-'
              }
            />
            <ManageableItemsList.Table.Column
              field="lineItem"
              header="Description"
              render={(lineItem: EstimateDefaultItem['lineItem']) =>
                lineItem.description?.trim() ? (
                  <div className="line-clamp-1 [&_p]:m-0 [&_p]:inline">
                    <MarkdownCore markdown={lineItem.description} />
                  </div>
                ) : (
                  '-'
                )
              }
            />
            <ManageableItemsList.Table.Column
              field="lineItem"
              header="Price (ex tax)"
              render={(lineItem: EstimateDefaultItem['lineItem']) => {
                const price = lineItem.price?.unitPriceExcTax;
                if (!price) return '-';
                return (
                  <CurrencyValue
                    value={price.value}
                    currency={price.currency}
                  />
                );
              }}
            />
            <ManageableItemsList.Table.Column
              field="quantity"
              header="Qty"
              render={(_quantity, row: EstimateDefaultItem) => (
                <QuantityCell
                  item={row}
                  isEditing={editingItemId === row.id}
                  onCommit={() => setEditingItemId(null)}
                />
              )}
            />
          </ManageableItemsList.Table>
        </ManageableItemsList>

        <DeleteEstimateDefaultItemModal
          isOpen={itemToDelete !== null}
          onClose={() => setItemToDelete(null)}
          item={itemToDelete}
        />

        <SelectItemModal
          isOpen={isSelectItemModalOpen}
          disabledItemIds={disabledItemIds}
          allowRepeatedItems={false}
          onClose={() => setIsSelectItemModalOpen(false)}
          onSelectItem={async (lineItemId) => {
            await onSelectItem(String(lineItemId));
            setIsSelectItemModalOpen(false);
          }}
          onCreateNewItem={() => {
            setIsSelectItemModalOpen(false);
            setIsCreateItemSidebarOpen(true);
          }}
        />

        <AddEditItemSidebar
          isOpen={isCreateItemSidebarOpen}
          onClose={() => setIsCreateItemSidebarOpen(false)}
          onCreateSuccess={async (lineItemId) => {
            await onSelectItem(lineItemId);
          }}
        />
      </Card.Body>
    </Card>
  );
};

export default DefaultEstimateItemsSection;
